// BenignIpAllowlistFromAlertModal — quick-suppress modal opened from the Detection
// Alerts Actions column (v1.114.0). Adds the alert's source IP to the GLOBAL benign-IP
// allowlist (DefenderPassthroughIPAllowlist, managed at /settings/benign-ip-allowlist),
// which backs the skip_if_in_defender_passthrough_ip_allowlist suppression tier.
//
// Unlike the (user, app) / (group, app) modals this is NOT scoped to a rule or tenant:
// the allowlist is a single global exact-IP list, so a match suppresses the
// Microsoft-security pass-through tier for EVERY rule that has the toggle on, across
// ALL tenants. The blast-radius note makes that explicit before the analyst confirms.
// A known-malicious IP still alerts (override_if_malicious_ip punches through).
//
// The IP is pre-filled from the alert (port stripped) and canonicalized to match the
// pipeline byte-for-byte via the same tryCanonicalizeIp the manager page uses. POSTs
// via addDefenderPassthroughIpEntry; the server canonicalizes again as a backstop.

import React, { useState, useMemo } from 'react';
import {
  XMarkIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { addDefenderPassthroughIpEntry } from '../../api/client';
import { stripPort } from '../sourceEvents/sharedHelpers';
import { tryCanonicalizeIp } from '../DefenderPassthroughIpAllowlistManager';

export default function BenignIpAllowlistFromAlertModal({ alert, onClose }) {
  const rawIp = stripPort(alert?.clientIp || '') || '';
  const canon = useMemo(() => tryCanonicalizeIp(rawIp), [rawIp]);

  const [reason, setReason] = useState('');
  const [sourceService, setSourceService] = useState('');
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);
  const [applied, setApplied] = useState(null);

  const trimmedReason = reason.trim();

  const blocker = useMemo(() => {
    if (!canon.ok) return rawIp ? canon.error : 'This alert has no usable source IP.';
    if (!trimmedReason) return 'Reason is required.';
    if (trimmedReason.length > 2000) return 'Reason exceeds 2000 chars.';
    if (sourceService.trim().length > 256) return 'Source service exceeds 256 chars.';
    return null;
  }, [canon, rawIp, trimmedReason, sourceService]);

  const handleApply = async () => {
    if (blocker) { setError(blocker); return; }
    setApplying(true);
    setError(null);
    try {
      const res = await addDefenderPassthroughIpEntry({
        ip:            canon.canonical,
        reason:        trimmedReason,
        sourceService: sourceService.trim(),
        enabled:       true,
      });
      setApplied({ created: !!res?.created, pendingSync: !!res?.pendingSync });
    } catch (err) {
      setError(err.message || 'Failed to add allowlist entry');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-lg w-full max-w-xl overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-800">
          <div>
            <h2 className="text-lg font-semibold text-gray-100">Add IP to benign-IP allowlist</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {alert?.ruleName ? <span className="text-gray-400">{alert.ruleName}</span> : 'Microsoft-security pass-through suppression'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300 p-1">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {applied ? (
            <div className="p-4 bg-emerald-900/20 border border-emerald-700/40 rounded-lg text-sm text-emerald-200 flex items-start gap-3">
              <CheckCircleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">{applied.created ? 'IP allowlisted.' : 'Entry updated (already existed).'}</p>
                <p className="text-emerald-300/80 mt-1">
                  <span className="font-mono">{canon.canonical}</span> is on the global benign-IP allowlist —
                  pass-through alerts from this IP will be suppressed on the next detection run.
                  {applied.pendingSync && ' The sentinel bump is pending retry (~30 s); the pipeline will pick it up shortly.'}
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Blast radius */}
              <div className="p-3 bg-gray-800/60 border border-gray-700/60 rounded text-xs text-gray-400 flex items-start gap-2">
                <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 mt-0.5 text-gray-500" />
                <span>
                  This adds the IP to the <span className="font-medium text-amber-300">global</span> benign-IP allowlist —
                  it suppresses the Microsoft-security pass-through tier for <span className="font-medium text-amber-300">every rule
                  that has this toggle on, across all tenants</span> (not just this rule/tenant). A known-malicious IP still alerts.
                </span>
              </div>

              {/* IP (read-only, canonicalized preview) */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">IP address</label>
                <div className="input-field w-full font-mono text-xs bg-gray-800/60 text-gray-300 flex items-center h-10">
                  {rawIp || <span className="italic text-gray-500">no source IP on this alert</span>}
                </div>
                {rawIp && (
                  canon.ok ? (
                    <p className="text-[11px] text-gray-500 mt-1">Stored as: <span className="font-mono text-gray-400">{canon.canonical}</span></p>
                  ) : (
                    <p className="text-[11px] text-red-400 mt-1">{canon.error}</p>
                  )
                )}
              </div>

              {/* Reason */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Reason</label>
                <input type="text" value={reason} onChange={(e) => setReason(e.target.value)}
                  placeholder="why this IP is trusted (e.g. OncoEMR SaaS egress; MS FP)" className="input-field w-full" />
              </div>

              {/* Source service (optional) */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Source service <span className="text-gray-600 normal-case font-normal">(optional)</span></label>
                <input type="text" value={sourceService} onChange={(e) => setSourceService(e.target.value)}
                  placeholder="azureAdIdentityProtection" className="input-field w-full font-mono text-xs" />
              </div>

              {error && (
                <div className="p-3 bg-red-900/30 border border-red-700/50 rounded text-red-300 text-sm flex items-center gap-2">
                  <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              {!error && blocker && (
                <div className="p-3 bg-amber-900/20 border border-amber-700/40 rounded text-amber-300 text-xs flex items-center gap-2">
                  <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
                  <span>{blocker}</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-800">
          {applied ? (
            <button onClick={onClose} className="btn-primary px-4 py-1.5 text-sm">Done</button>
          ) : (
            <>
              <button onClick={onClose} disabled={applying} className="btn-secondary px-4 py-1.5 text-sm">Cancel</button>
              <button onClick={handleApply} disabled={applying || !!blocker} className="btn-primary px-4 py-1.5 text-sm flex items-center gap-2">
                {applying && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
                Add to allowlist
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
